import { PaletteMode, Components, Theme } from '@mui/material';
import { CSSProperties } from 'react';
import { themePalette } from '../theme.palette';
import { typographyTokens } from '../typography-tokens';

declare module '@mui/material/ToggleButton' {
  interface ToggleButtonPropsSizeOverrides {
    xlarge: true;
  }
  interface ToggleButtonClasses {
    sizeXlarge: string;
  }
}

export const MuiToggleButtonTheme = (mode: PaletteMode): Components<Theme>['MuiToggleButton'] => {
  const { componentTokens } = themePalette(mode);

  const commonStyles: CSSProperties = {
    transition:
      'border-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, background-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
    border: '1px solid',
    fontFamily: typographyTokens.fontFamilies.sans,
    fontWeight: typographyTokens.fontWeights.medium,
    textTransform: 'none',
    color: componentTokens.tabs.colors.contained.text.resting,
    borderRadius: '6px',
  };

  return {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        ...commonStyles,
        color: componentTokens.tabs.colors.contained.text.resting,
        backgroundColor: componentTokens.tabs.colors.contained.background.resting,
        borderColor: componentTokens.tabs.colors.contained.stroke.resting,
        '& .MuiSvgIcon-root': {
          color: componentTokens.tabs.colors.contained.assets.resting,
        },
        // Disable hover effects for unselected state
        '&:hover:not(.Mui-selected)': {
          color: componentTokens.tabs.colors.contained.text.hover,
          backgroundColor: componentTokens.tabs.colors.contained.background.hover,
          borderColor: componentTokens.tabs.colors.contained.stroke.hover,
          '& .MuiSvgIcon-root': {
            color: componentTokens.tabs.colors.contained.assets.hover,
          },
        },
        // Unselected focus state
        '&:focus-visible:not(.Mui-selected)': {
          color: componentTokens.tabs.colors.contained.text.hover,
          backgroundColor: componentTokens.tabs.colors.contained.background.hover,
          borderColor: componentTokens.tabs.colors.contained.stroke.hover,
          '& .MuiSvgIcon-root': {
            color: componentTokens.tabs.colors.contained.assets.hover,
          },
        },
        // Selected state
        '&.Mui-selected': {
          color: componentTokens.tabs.colors.contained.text.selected,
          backgroundColor: componentTokens.tabs.colors.contained.background.selected,
          borderColor: componentTokens.tabs.colors.contained.stroke.selected,
          '& .MuiSvgIcon-root': {
            color: componentTokens.tabs.colors.contained.assets.selected,
          },
          '&:hover': {
            color: componentTokens.tabs.colors.contained.text.selectedHover,
            backgroundColor: componentTokens.tabs.colors.contained.background.selectedHover,
            borderColor: componentTokens.tabs.colors.contained.stroke.selectedHover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.tabs.colors.contained.assets.selectedHover,
            },
          },
          '&:focus-visible': {
            color: componentTokens.tabs.colors.contained.text.selectedHover,
            backgroundColor: componentTokens.tabs.colors.contained.background.selectedHover,
            borderColor: componentTokens.tabs.colors.contained.stroke.selectedHover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.tabs.colors.contained.assets.selectedHover,
            },
          },
        },
        '&:disabled': {
          opacity: 0.4,
        },
      },
      // Size variants
      sizeSmall: {
        padding: '8px',
        minWidth: '28px',
        height: '28px',
        fontSize: typographyTokens.fontSizes.xs,
        lineHeight: 1,
        '& .MuiSvgIcon-root': {
          width: '12px',
          height: '12px',
        },
      },
      sizeMedium: {
        padding: '10px',
        minWidth: '32px',
        height: '32px',
        fontSize: typographyTokens.fontSizes.xs,
        lineHeight: 1,
        '& .MuiSvgIcon-root': {
          width: '14px',
          height: '14px',
        },
      },
      sizeLarge: {
        padding: '12px',
        minWidth: '40px',
        height: '40px',
        fontSize: typographyTokens.fontSizes.sm,
        lineHeight: 1,
        '& .MuiSvgIcon-root': {
          width: '16px',
          height: '16px',
        },
      },
      sizeXlarge: {
        padding: '12px',
        minWidth: '48px',
        height: '48px',
        fontSize: typographyTokens.fontSizes.lg,
        lineHeight: 1,
        '& .MuiSvgIcon-root': {
          width: '20px',
          height: '20px',
        },
      },
    },
  };
};
